import { AppHTTPService } from '@/api/http-service'
import { updateSheet } from '@/api/update-sheet'
import { OAuth } from '@/auth/oauth'
import { FieldsContext } from '@/contexts/FieldsContext'
import { Rule, useRulesContext, getPendingRules } from '@/contexts/RulesContext'
import { useSheet } from '@/contexts/SheetContext'
import { driver } from '@/data'
import {
  GetRecordsParams as DataGetRecordsParams,
  RegisterRecordUpdateParams,
  RowChangeDetail,
  RowPatchDetail,
} from '@/data/interface'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useContext } from 'react'

export interface RecordDataPayload {
  [key: string]: { value: any }
}

export interface GetRecordsParams {
  pageSize?: number
  pageNumber?: number
  sinceVersionId?: string
  searchFields?: { [key: string]: string }
  mode?: 'select' | 'analyze' | 'export'
}

type ColumnConfig = Array<{
  value: string
  label: string
  type: string
}>

const generateFreshRecordId = (sheetId: string) => {
  const buffer = new Uint8Array(16)
  crypto.getRandomValues(buffer)

  // Convert to hexadecimal string (32 characters)
  const uuid = Array.from(buffer)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
  const envPrefix = sheetId.split('_')[0]
  const prefix = 'rc'
  const recordKey = `${envPrefix}_${prefix}_${uuid}`
  return recordKey
}

export function useSheetRecords() {
  const { sheet } = useSheet()
  const queryClient = useQueryClient()
  const {
    pendingRules: rules,
    addPendingRules,
    clearPendingRules,
  } = useRulesContext()
  const {
    overridingConfig,
    setOverridingConfig,
    overridingRowCount,
    setOverridingRowCount,
    setExtraKeys,
    releaseLocks,
  } = useContext(FieldsContext)

  const refetchSheetCounts = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['sheetCounts', sheet?.id],
    })
  }, [queryClient, sheet?.id])

  /**
   * Fetches records with pagination and filtering support
   * Now uses the ephemeral driver's fetchRecords method
   */
  const fetchRecords = useCallback(
    async (params: GetRecordsParams, columnConfig: ColumnConfig) => {
      const augmentedColumnConfig = columnConfig.map((config) => ({
        ...config,
        sheetId: sheet?.id,
      }))
      // Convert our params to the data layer's GetRecordsParams
      const dataParams: DataGetRecordsParams = {
        sheetId: sheet?.id,
        pageSize: params.pageSize,
        pageNumber: params.pageNumber,
        sinceVersionId: params.sinceVersionId,
        searchFields: params.searchFields,
        transformationRules: rules
          .filter((rule) => rule.disabled !== true)
          .map((rule) => rule.config),
        setExtraKeys,
        mode: params.mode,
      }

      try {
        const result = await driver.fetchRecords(
          dataParams,
          augmentedColumnConfig
        )

        return result
      } catch (error) {
        console.error('Error fetching records:', error)
        return []
      }
    },
    [
      sheet,
      overridingConfig,
      overridingRowCount,
      setOverridingConfig,
      setOverridingRowCount,
      rules,
      setExtraKeys,
    ]
  )

  /**
   * Adds new records to the sheet
   */
  const addRecords = useCallback(
    async (changes: RowChangeDetail[], columnConfig: any[]) => {
      return driver.addRecords(sheet.id, changes, columnConfig)
    },
    []
  )

  /**
   * Updates existing records in the sheet
   */
  const updateRecords = useCallback(
    async (changes: RowChangeDetail[], columnConfig: any[]) => {
      const result = await driver.updateRecords(sheet.id, changes, columnConfig)
      refetchSheetCounts()
      return result
    },
    [refetchSheetCounts]
  )

  /**
   * Deletes records from the sheet
   */
  const deleteRecords = useCallback(
    async (recordIds: string[]) => {
      const result = await driver.deleteRecords({
        sheetId: sheet?.id,
        recordIds,
      })
      refetchSheetCounts()
      return result
    },
    [sheet?.id, refetchSheetCounts]
  )

  /**
   * Downloads records as CSV
   */
  const downloadRecords = useCallback(
    async (filter?: string) => {
      return driver.downloadRecords({ sheetId: sheet?.id, filter })
    },
    [sheet?.id]
  )

  /**
   * Adds or updates records in a batch operation
   */
  const addOrUpdateRecords = useCallback(
    async (
      changeEvent: { changes: RowPatchDetail[] },
      columnConfig: ColumnConfig
    ) => {
      const newRule = {
        name: 'Apply Changes',
        type: 'apply-changes',
        config: {
          type: 'apply-changes',
          changes: changeEvent.changes.map((change) => ({
            ...change,
            generatedId: change.id
              ? undefined
              : generateFreshRecordId(sheet?.id),
          })),
        },
      } as Rule
      addPendingRules([newRule])
      releaseLocks()

      await AppHTTPService.client.sheets.update(sheet?.id, {
        addedRules: [newRule],
      })
      // return driver.addOrUpdateRecords(sheet.id, changeEvent, columnConfig)
    },
    [sheet?.id, refetchSheetCounts, releaseLocks, addPendingRules, rules]
  )

  const hydrateAllRecords = useCallback(async () => {
    const result = await driver.hydrateAllRecords({
      sheetId: sheet?.id,
      sheet,
    })

    refetchSheetCounts()
    return result
  }, [sheet, refetchSheetCounts, rules])

  /**
   * Commits pending transformation rules to the Sheet object permanently
   */
  const commitPendingRulesToSheet = useCallback(async () => {
    if (!sheet?.id || rules.length === 0) {
      return { success: false, message: 'No pending rules to commit' }
    }

    try {
      // Get current sheet rules and merge with pending rules
      const currentRules = sheet.rules || []
      const allRules = [...currentRules, ...rules.map((rule) => rule.config)]

      // Update the sheet with the new rules
      const updatedSheet = await updateSheet({
        sheetId: sheet.id,
        token: OAuth.TOKEN,
        rules: allRules,
      })

      // Clear pending rules after successful commit
      clearPendingRules()

      return { success: true, sheet: updatedSheet }
    } catch (error) {
      console.error('Error committing rules to sheet:', error)
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }, [sheet, rules, clearPendingRules])

  const commitCurrentChanges = useCallback(
    async (columnConfig: ColumnConfig) => {
      return driver
        .commitCurrentChanges({
          sheetId: sheet?.id,
          transformationRules: rules
            .filter((rule) => rule.disabled !== true)
            .map((rule) => rule.config),
          columnConfig: columnConfig,
        })
        .then(() => {
          clearPendingRules()
        })
    },
    [sheet?.id, rules, clearPendingRules]
  )

  const registerRecordUpdate = useCallback(
    async (params: RegisterRecordUpdateParams) => {
      return driver.registerRecordUpdate(params)
    },
    []
  )

  const getRecordById = useCallback(
    async (params: { sheetId: string; recordId: string }) => {
      return driver.getRecordById(params)
    },
    []
  )

  return {
    fetchRecords,
    addRecords,
    updateRecords,
    deleteRecords,
    downloadRecords,
    addOrUpdateRecords,
    hydrateAllRecords,
    commitCurrentChanges,
    registerRecordUpdate,
    commitPendingRulesToSheet,
    getRecordById,
  }
}
